import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import JsBarcode from 'jsbarcode';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, GlobalStyles, Typography } from '@mui/material';
import { PrintRounded as PrintIcon, CloseRounded as CloseIcon, Restaurant as RestaurantIcon } from '@mui/icons-material';
import { GuestToken, GuestTokensResult, getImageUrl, reservationsAPI } from '../../services/api';
import { toPersianNumber } from '../../utils/persianNumber';
import { formatPersianDate } from '../../utils/dateHelper';
import { readGuestTokenBatch } from '../../utils/guestTokenPrint';

const MEAL_LABELS: Record<string, string> = { breakfast: 'صبحانه', lunch: 'ناهار', dinner: 'شام' };
const FONT = 'Vazir, sans-serif';

// Code128 bars for the delivery code; the scanner reads the bars, people read the digits below
const Barcode: React.FC<{ value: string }> = ({ value }) => {
    const ref = useRef<SVGSVGElement>(null);
    useEffect(() => {
        if (!ref.current) return;
        JsBarcode(ref.current, value, { format: 'CODE128', width: 2, height: 56, margin: 0, displayValue: false, background: '#ffffff', lineColor: '#000000' });
    }, [value]);
    return <svg ref={ref} role="img" aria-label={`بارکد ${value}`} style={{ width: '100%', height: '15mm', display: 'block' }} preserveAspectRatio="none" />;
};

const groupCode = (code: string) => toPersianNumber(code.replace(/(\d{4})(?=\d)/g, '$1 '));

const TokenCard: React.FC<{ token: GuestToken; index: number; total: number; restaurant: GuestTokensResult['restaurant'] }> = ({ token, index, total, restaurant }) => (
    <Box
        component="article"
        sx={{
            width: '72mm', boxSizing: 'border-box', p: '3.5mm', border: '1.5px dashed #64748b', borderRadius: '3mm',
            bgcolor: '#fff', color: '#0f172a', fontFamily: FONT, breakInside: 'avoid', pageBreakInside: 'avoid',
            display: 'flex', flexDirection: 'column', gap: '2mm',
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2mm', borderBottom: '1px solid #cbd5e1', pb: '2mm' }}>
            {restaurant.logoUrl && (
                <Box component="img" src={getImageUrl(restaurant.logoUrl)} alt="" sx={{ height: '8mm', maxWidth: '16mm', objectFit: 'contain', filter: 'grayscale(1)' }} />
            )}
            <Typography sx={{ color: '#0f172a', fontFamily: FONT, fontWeight: 800, fontSize: '10pt', flex: 1, lineHeight: 1.3 }}>{restaurant.name || 'رستوران'}</Typography>
            <Box component="span" sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '8pt', border: '1px solid #0f172a', borderRadius: '2mm', px: '1.5mm', py: '0.5mm', whiteSpace: 'nowrap' }}>
                ژتون مهمان
            </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2.5mm' }}>
            <Box
                sx={{ width: '15mm', height: '15mm', flexShrink: 0, borderRadius: '2mm', overflow: 'hidden', bgcolor: '#e2e8f0', display: 'grid', placeItems: 'center' }}
            >
                {token.mealImage
                    ? <Box component="img" src={getImageUrl(token.mealImage)} alt={token.mealTitle} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <RestaurantIcon sx={{ color: '#64748b' }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#0f172a', fontFamily: FONT, fontWeight: 900, fontSize: '12pt', lineHeight: 1.35 }}>{token.mealTitle}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: '9pt', color: '#334155' }}>
                    {MEAL_LABELS[token.mealType]} · {formatPersianDate(token.mealDate)}
                </Typography>
            </Box>
        </Box>

        <Box sx={{ fontSize: '9pt', lineHeight: 1.6 }}>
            <Typography sx={{ color: '#0f172a', fontFamily: FONT, fontSize: 'inherit' }}>
                واحد: <b>{token.unitTitle}</b>
            </Typography>
            {token.description && (
                <Typography sx={{ fontFamily: FONT, fontSize: 'inherit', color: '#334155', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {token.description}
                </Typography>
            )}
        </Box>

        {token.deliveryCode && (
            <Box>
                <Barcode value={token.deliveryCode} />
                <Typography dir="ltr" sx={{ color: '#0f172a', fontFamily: FONT, fontWeight: 800, fontSize: '11pt', letterSpacing: '1px', textAlign: 'center', mt: '1mm' }}>
                    {groupCode(token.deliveryCode)}
                </Typography>
            </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '2mm', fontSize: '7.5pt', color: '#475569', borderTop: '1px solid #cbd5e1', pt: '1.5mm' }}>
            <span>فقط برای همین روز و وعده · یک‌بار مصرف</span>
            <span>پرس {toPersianNumber(index)} از {toPersianNumber(total)}</span>
        </Box>
    </Box>
);

const GuestTokenPrint: React.FC = () => {
    const [params] = useSearchParams();
    const ids = useMemo(() => readGuestTokenBatch(params.get('batch')), [params]);
    const [data, setData] = useState<GuestTokensResult | null>(null);
    const [error, setError] = useState('');
    const [includeDelivered, setIncludeDelivered] = useState(false);
    const printedRef = useRef(false);

    useEffect(() => {
        document.title = 'چاپ ژتون مهمان';
        if (ids.length === 0) {
            setError('ژتونی برای چاپ انتخاب نشده است. از صفحه‌ی «رزرو مهمان» دوباره اقدام کنید.');
            return;
        }
        reservationsAPI.getGuestTokens(ids)
            .then(setData)
            .catch((err) => setError(err.response?.data?.error || 'دریافت اطلاعات ژتون‌ها ناموفق بود'));
    }, [ids]);

    const printable = useMemo(
        () => (data?.tokens ?? []).filter((t) => !t.isCancelled && t.deliveryCode && (includeDelivered || !t.isDelivered)),
        [data, includeDelivered],
    );
    const cancelled = (data?.tokens ?? []).filter((t) => t.isCancelled).length;
    const deliveredCount = (data?.tokens ?? []).filter((t) => !t.isCancelled && t.isDelivered).length;

    // "portion i of n" within each order (same day, meal, dish, unit and note)
    const numbering = useMemo(() => {
        const key = (t: GuestToken) => [t.mealDate, t.mealType, t.mealTitle, t.unitTitle, t.description || ''].join('|');
        const totals = new Map<string, number>();
        (data?.tokens ?? []).filter((t) => !t.isCancelled).forEach((t) => totals.set(key(t), (totals.get(key(t)) || 0) + 1));
        const seen = new Map<string, number>();
        const result = new Map<string, { index: number; total: number }>();
        (data?.tokens ?? []).filter((t) => !t.isCancelled).forEach((t) => {
            const n = (seen.get(key(t)) || 0) + 1;
            seen.set(key(t), n);
            result.set(t.id, { index: n, total: totals.get(key(t)) || n });
        });
        return result;
    }, [data]);

    // Print once automatically, after barcodes are drawn and photos have loaded (or 1.5 s at most)
    useEffect(() => {
        if (!data || printable.length === 0 || printedRef.current) return;
        printedRef.current = true;
        const images = Array.from(document.querySelectorAll<HTMLImageElement>('article img'));
        const loaded = Promise.all(images.map((img) => (img.complete ? Promise.resolve() : new Promise((r) => { img.onload = r; img.onerror = r; }))));
        const timeout = new Promise((r) => setTimeout(r, 1500));
        Promise.race([loaded, timeout]).then(() => window.print());
    }, [data, printable.length]);

    return (
        <Box dir="rtl" sx={{ minHeight: '100vh', bgcolor: '#e2e8f0', color: '#0f172a', fontFamily: FONT, '@media print': { bgcolor: '#fff' } }}>
            <GlobalStyles styles={{
                '@page': { size: 'auto', margin: '5mm' },
                '@media print': { body: { background: '#fff !important' }, '.no-print': { display: 'none !important' } },
            }} />

            <Box className="no-print" sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: '#0f172a', color: '#fff', px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography component="h1" sx={{ fontFamily: FONT, fontWeight: 900, fontSize: '1.2rem', flex: 1 }}>
                    چاپ ژتون مهمان {data && `· ${toPersianNumber(printable.length)} ژتون`}
                </Typography>
                {deliveredCount > 0 && (
                    <FormControlLabel
                        control={<Checkbox checked={includeDelivered} onChange={(e) => setIncludeDelivered(e.target.checked)} sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#fff' } }} />}
                        label={<Typography sx={{ fontFamily: FONT }}>چاپ {toPersianNumber(deliveredCount)} ژتونِ تحویل‌شده هم</Typography>}
                    />
                )}
                <Button
                    onClick={() => window.print()}
                    disabled={printable.length === 0}
                    variant="contained"
                    startIcon={<PrintIcon />}
                    sx={{ height: 48, borderRadius: '12px', fontFamily: FONT, fontWeight: 800, bgcolor: '#4a6ce8' }}
                >
                    چاپ
                </Button>
                <Button onClick={() => window.close()} startIcon={<CloseIcon />} sx={{ height: 48, borderRadius: '12px', fontFamily: FONT, color: '#fff' }}>
                    بستن
                </Button>
            </Box>

            {(cancelled > 0 || (deliveredCount > 0 && !includeDelivered)) && (
                <Box className="no-print" sx={{ px: 3, pt: 2 }}>
                    <Typography sx={{ fontFamily: FONT, color: '#475569' }}>
                        {cancelled > 0 && `${toPersianNumber(cancelled)} رزرو لغوشده چاپ نمی‌شود. `}
                        {deliveredCount > 0 && !includeDelivered && `${toPersianNumber(deliveredCount)} ژتون قبلاً استفاده شده و چاپ نمی‌شود.`}
                    </Typography>
                </Box>
            )}

            {!data && !error && (
                <Box sx={{ display: 'grid', placeItems: 'center', py: 12 }}><CircularProgress /></Box>
            )}
            {error && (
                <Typography sx={{ fontFamily: FONT, textAlign: 'center', py: 10, color: '#b91c1c' }}>{error}</Typography>
            )}
            {data && printable.length === 0 && !error && (
                <Typography sx={{ color: '#0f172a', fontFamily: FONT, textAlign: 'center', py: 10 }}>ژتون قابل چاپی وجود ندارد.</Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4mm', p: '6mm', '@media print': { p: 0, gap: '3mm', justifyContent: 'flex-start' } }}>
                {data && printable.map((t) => {
                    const n = numbering.get(t.id) || { index: 1, total: 1 };
                    return <TokenCard key={t.id} token={t} index={n.index} total={n.total} restaurant={data.restaurant} />;
                })}
            </Box>
        </Box>
    );
};

export default GuestTokenPrint;
